import React, { FC, ReactElement, useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import MainLayout from "../Layout/MainLayout"
import { InfoSection, InfoRow } from "../Components/BookingDetail/InfoSection"
import PreferredSlots from "../Components/BookingDetail/PreferredSlots"
import ActionButtons from "../Components/BookingDetail/ActionButtons"
import ConfirmModal from "../Components/BookingDetail/ConfirmModal"
import MessageModal from "../Components/BookingDetail/MessageModal"
import { 
  getBookingDetail,
  approvePreferredSlot,
  updateBooking,
  changeBookingStatus
} from "../Api/BookingApi"
import { getTimeSlotLabel } from "../../Enums/TimeSlot"
import type { BookingDetail as BookingDetailType } from "../../Types/BookingTypes"

const BookingDetail: FC = (): ReactElement => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [booking, setBooking] = useState<BookingDetailType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  
  // 編集フォームの状態
  const [formData, setFormData] = useState<any>(null)
  
  // モーダル状態
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    confirmText?: string
    cancelText?: string
    onConfirm: () => void
    variant?: 'default' | 'danger'
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  })
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean
    message: string
    type?: 'success' | 'error'
  }>({
    isOpen: false,
    message: '',
    type: 'success'
  })
  
  // 承認手続き用の状態管理
  const [paymentCheckModal, setPaymentCheckModal] = useState<{
    isOpen: boolean
    slotId: number | null
  }>({
    isOpen: false,
    slotId: null
  })
  const [attendeeSelectionModal, setAttendeeSelectionModal] = useState<{
    isOpen: boolean
    slotId: number | null
    requiresAttendee: boolean | null
  }>({
    isOpen: false,
    slotId: null,
    requiresAttendee: null
  })

  useEffect(() => {
    const fetchBookingDetail = async () => {
      if (!id) {
        setError('予約IDが指定されていません。')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        const bookingData = await getBookingDetail(id)
        setBooking(bookingData)
        // 編集フォーム用の初期データを設定
        setFormData({
          type: bookingData.type,
          contractor: { ...bookingData.contractor },
          serviceAddress: { ...bookingData.serviceAddress },
          customerNumber: { ...bookingData.customerNumber },
          consumer: { ...bookingData.consumer },
          attendee: { ...bookingData.attendee },
          preferredSlots: bookingData.preferredSlots.length > 0
            ? bookingData.preferredSlots.map(slot => ({
                date: slot.date ? (slot.date.includes('T') ? slot.date.split('T')[0] : slot.date) : '',
                time_slot: slot.timeSlot || '',
                preference_order: slot.preferenceOrder || null,
                is_fixed: slot.isFixed || false
              }))
            : [{ date: '', time_slot: '', preference_order: null, is_fixed: false }],
          broughtAppliances: bookingData.broughtAppliances.length > 0
            ? bookingData.broughtAppliances.map(app => ({
                type: app.type || ''
              }))
            : [{ type: '' }]
        })
      } catch (err) {
        console.error('Failed to fetch booking detail:', err)
        if (err instanceof Error) {
          setError(err.message)
        } else {
          setError('予約データの取得に失敗しました。')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchBookingDetail()
  }, [id])

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      approved: '承認済み',
      unapproved: '未承認',
      callout: '架電予定',
      canceled: 'キャンセル',
      is_no_show: '立会ドタキャン'
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      approved: 'bg-green-100 text-green-800',
      unapproved: 'bg-yellow-100 text-yellow-800',
      callout: 'bg-blue-100 text-blue-800',
      canceled: 'bg-red-100 text-red-800',
      is_no_show: 'bg-red-100 text-red-800'
    }
    return colorMap[status] || 'bg-gray-100 text-gray-800'
  }

  const getTypeLabel = (type: string): string => {
    return type === 'open' ? '開栓' : '閉栓'
  }

  const getRelationToContractorLabel = (relation: string | null | undefined): string => {
    if (!relation) return '-'
    const relationMap: Record<string, string> = {
      self: '本人',
      family: '家族',
      landlord: '大家',
      manager: '管理人',
      other: 'その他'
    }
    return relationMap[relation] || relation
  }

  const getPaymentMethodLabel = (method: string | null | undefined): string => {
    if (!method) return '-'
    const methodMap: Record<string, string> = {
      direct_debit: '口座振替',
      bank_transfer: '銀行振込',
      convenience_store: 'コンビニ払い',
      invoice: '請求書',
      other: 'その他'
    }
    return methodMap[method] || method
  }

  const getBooleanLabel = (value: boolean | null | undefined): string => {
    if (value === null || value === undefined) return '-'
    return value ? 'あり' : 'なし'
  }

  const getApplianceTypeLabel = (type: string | null | undefined): string => {
    if (!type) return '-'
    const typeMap: Record<string, string> = {
      gas_stove: 'ガスコンロ',
      gas_water_heater: 'ガス給湯器',
      gas_heating: 'ガス暖房',
      other: 'その他'
    }
    return typeMap[type] || type
  }

  const getBuildingTypeLabel = (type: string | null | undefined): string => {
    if (!type) return '-'
    const typeMap: Record<string, string> = {
      house: '戸建て',
      mansion: 'マンション',
      apartment: 'アパート',
      other: 'その他'
    }
    return typeMap[type] || type
  }

  const formatBillingAddress = (address: string | null | undefined): string => {
    if (!address) return '-'
    
    // 郵便番号パターン（3桁-4桁）で先頭を抽出
    const zipCodePattern = /^(\d{3}-\d{4})\s+(.+)$/;
    const match = address.match(zipCodePattern);
    
    if (match) {
      const zipCode = match[1];
      const rest = match[2];
      
      // 残りを半角スペースで分割
      const parts = rest.split(/\s+/);
      
      if (parts.length >= 2) {
        // 最後の2つの部分を名前として扱う（姓と名）
        const name = parts.slice(-2).join(' ');
        // それより前の部分を住所として結合
        const addressLine = parts.slice(0, -2).join(' ');
        
        if (addressLine) {
          return `${zipCode}\n${addressLine}\n${name}`;
        } else {
          // 住所がない場合（通常はないが念のため）
          return `${zipCode}\n${name}`;
        }
      } else {
        // 1つの部分しかない場合
        return `${zipCode}\n${rest}`;
      }
    } else {
      // 郵便番号パターンが見つからない場合、元の処理にフォールバック
      // ただし、最後の2つの部分を名前として扱う
      const parts = address.split(/\s+/);
      if (parts.length >= 2) {
        const name = parts.slice(-2).join(' ');
        const addressLine = parts.slice(0, -2).join(' ');
        if (addressLine) {
          return `${addressLine}\n${name}`;
        } else {
          return name;
        }
      } else {
        return address;
      }
    }
  }

  const formatBookingId = (id: number): string => {
    return 'BK' + String(id).padStart(3, '0')
  }

  const handleApproveSlot = (slotId: number) => {
    if (!booking || !id) return
    
    const slot = booking.preferredSlots.find(s => s.id === slotId)
    if (!slot) return

    // 閉栓予約の場合のみ料金未払い・リース契約の確認モーダルを表示
    if (booking.type === 'close') {
      setPaymentCheckModal({
        isOpen: true,
        slotId: slotId
      })
    } else {
      // 開栓予約の場合は直接最終確認モーダルへ
      showFinalConfirmationModal(slotId, null)
    }
  }

  // 料金確認モーダルで「承認へ進む」を押したとき
  const handleProceedToAttendeeSelection = () => {
    if (paymentCheckModal.slotId === null || !booking) return
    
    setPaymentCheckModal({ ...paymentCheckModal, isOpen: false })
    
    // 閉栓予約の場合のみ立合い選択モーダルを表示
    if (booking.type === 'close') {
      setAttendeeSelectionModal({
        isOpen: true,
        slotId: paymentCheckModal.slotId,
        requiresAttendee: null
      })
    } else {
      // 開栓予約の場合は直接最終確認モーダルへ
      showFinalConfirmationModal(paymentCheckModal.slotId, null)
    }
  }

  // 立合い選択モーダルで「次へ」を押したとき（閉栓予約のみ）
  const handleProceedToFinalConfirmation = () => {
    if (attendeeSelectionModal.slotId === null || attendeeSelectionModal.requiresAttendee === null) return
    
    showFinalConfirmationModal(attendeeSelectionModal.slotId, attendeeSelectionModal.requiresAttendee)
    setAttendeeSelectionModal({ ...attendeeSelectionModal, isOpen: false })
  }

  // 最終確認モーダルを表示する関数
  const showFinalConfirmationModal = (slotId: number, requiresAttendee: boolean | null) => {
    if (!booking || !id) return
    
    const slot = booking.preferredSlots.find(s => s.id === slotId)
    if (!slot) return

    const dateStr = slot.date ? formatDate(slot.date) : ''
    const timeStr = slot.timeSlot ? getTimeSlotLabel(slot.timeSlot) : ''
    
    // 閉栓予約の場合のみ立合い情報を追加
    let message = `${dateStr} ${timeStr}`
    if (booking.type === 'close' && requiresAttendee !== null) {
      const attendeeText = requiresAttendee ? '立合いあり' : '立合いなし'
      message += `\n${attendeeText}`
    }
    message += ' で予約を承認しますか？'
    
    // 最終確認モーダルを表示
    setConfirmModal({
      isOpen: true,
      title: '予約の承認',
      message: message,
      confirmText: '承認する',
      cancelText: 'キャンセル',
      variant: 'default',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false })
        try {
          setLoading(true)
          // 閉栓予約の場合はrequires_attendeeを送信、開栓予約の場合はデフォルト値（true）を送信
          const requiresAttendeeValue = booking.type === 'close' && requiresAttendee !== null 
            ? requiresAttendee 
            : true
          await approvePreferredSlot(id, slotId, requiresAttendeeValue)
          
          // データを再取得
          const bookingData = await getBookingDetail(id)
          setBooking(bookingData)
          setMessageModal({
            isOpen: true,
            message: '予約が承認されました。',
            type: 'success'
          })
        } catch (err) {
          console.error('Failed to approve slot:', err)
          setMessageModal({
            isOpen: true,
            message: err instanceof Error ? err.message : '承認処理に失敗しました。',
            type: 'error'
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const handleStatusChange = (newStatus: string) => {
    if (!id) return
    
    const statusText = getStatusLabel(newStatus)
    
    setConfirmModal({
      isOpen: true,
      title: 'ステータスの変更',
      message: `ステータスを「${statusText}」に変更しますか？`,
      confirmText: '変更する',
      cancelText: 'キャンセル',
      variant: 'default',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false })
        try {
          setLoading(true)
          await changeBookingStatus(id, newStatus)
          
          // データを再取得
          const bookingData = await getBookingDetail(id)
          setBooking(bookingData)
          setMessageModal({
            isOpen: true,
            message: `ステータスを「${statusText}」に変更しました。`,
            type: 'success'
          })
        } catch (err) {
          console.error('Failed to change status:', err)
          setMessageModal({
            isOpen: true,
            message: err instanceof Error ? err.message : 'ステータスの変更に失敗しました。',
            type: 'error'
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const handleEdit = () => {
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setConfirmModal({
      isOpen: true,
      title: '編集のキャンセル',
      message: '編集をキャンセルしますか？変更内容は保存されません。',
      confirmText: 'キャンセルする',
      cancelText: '続ける',
      variant: 'default',
      onConfirm: () => {
        setConfirmModal({ ...confirmModal, isOpen: false })
        setIsEditMode(false)
        // フォームデータを元のデータにリセット
        if (booking) {
          setFormData({
            type: booking.type,
            contractor: { ...booking.contractor },
            serviceAddress: { ...booking.serviceAddress },
            customerNumber: { ...booking.customerNumber },
            consumer: { ...booking.consumer },
            attendee: { ...booking.attendee },
            preferredSlots: booking.preferredSlots.length > 0
              ? booking.preferredSlots.map(slot => ({
                  date: slot.date ? (slot.date.includes('T') ? slot.date.split('T')[0] : slot.date) : '',
                  time_slot: slot.timeSlot || '',
                  preference_order: slot.preferenceOrder || null,
                  is_fixed: slot.isFixed || false
                }))
              : [{ date: '', time_slot: '', preference_order: null, is_fixed: false }],
            broughtAppliances: booking.broughtAppliances.length > 0
              ? booking.broughtAppliances.map(app => ({
                  type: app.type || ''
                }))
              : [{ type: '' }]
          })
        }
      }
    })
  }

  const handleUpdate = async () => {
    if (!id || !formData) return

    try {
      setLoading(true)
      
      // API用のデータ形式に変換
      const updatePayload: any = {}
      
      if (formData.type) updatePayload.type = formData.type
      if (formData.contractor) updatePayload.contractor = {
        name: formData.contractor.name || '',
        name_kana: formData.contractor.nameKana || '',
        birthday: formData.contractor.birthday || null,
        phone_number: formData.contractor.phoneNumber || '',
        email: formData.contractor.email || '',
        alt_billing_address: formData.contractor.altBillingAddress || '',
        payment_method: formData.contractor.paymentMethod || null,
        notes: formData.contractor.notes || ''
      }
      if (formData.serviceAddress) updatePayload.service_address = {
        zip_code: formData.serviceAddress.zipCode || '',
        municipality: formData.serviceAddress.municipality || '',
        address_line: formData.serviceAddress.addressLine || '',
        building: formData.serviceAddress.building || null,
        building_type: formData.serviceAddress.buildingType || null
      }
      if (formData.customerNumber) updatePayload.customer_number = {
        customer_number: formData.customerNumber.customerNumber || null
      }
      if (formData.consumer) updatePayload.consumer = {
        relation_to_contractor: formData.consumer.relationToContractor || null,
        name: formData.consumer.name || '',
        name_kana: formData.consumer.nameKana || '',
        birthday: formData.consumer.birthday || null,
        phone_number: formData.consumer.phoneNumber || ''
      }
      if (formData.attendee) updatePayload.attendee = {
        name: formData.attendee.name || '',
        name_kana: formData.attendee.nameKana || '',
        phone_number: formData.attendee.phoneNumber || '',
        relation_to_contractor: formData.attendee.relationToContractor || null
      }
      if (formData.preferredSlots) {
        updatePayload.preferred_slots = formData.preferredSlots
          .filter((slot: any) => slot.date && slot.time_slot)
          .map((slot: any) => ({
            date: slot.date,
            time_slot: slot.time_slot,
            preference_order: slot.preference_order || null,
            is_fixed: slot.is_fixed || false
          }))
      }
      if (formData.broughtAppliances) {
        updatePayload.brought_appliances = formData.broughtAppliances
          .filter((app: any) => app.type)
          .map((app: any) => ({
            type: app.type
          }))
      }

      const updatedBooking = await updateBooking(id, updatePayload)
      setBooking(updatedBooking)
      setIsEditMode(false)
      setMessageModal({
        isOpen: true,
        message: '予約内容が更新されました。',
        type: 'success'
      })
    } catch (err) {
      console.error('Failed to update booking:', err)
      setMessageModal({
        isOpen: true,
        message: err instanceof Error ? err.message : '予約内容の更新に失敗しました。',
        type: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const updateFormField = (section: string, field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }))
  }

  const updateFormArray = (section: string, index: number, field: string, value: any) => {
    setFormData((prev: any) => {
      const newArray = [...prev[section]]
      newArray[index] = {
        ...newArray[index],
        [field]: value
      }
      return {
        ...prev,
        [section]: newArray
      }
    })
  }

  const addPreferredSlot = () => {
    setFormData((prev: any) => ({
      ...prev,
      preferredSlots: [
        ...prev.preferredSlots,
        { date: '', time_slot: '', preference_order: null, is_fixed: false }
      ]
    }))
  }

  const removePreferredSlot = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      preferredSlots: prev.preferredSlots.filter((_: any, i: number) => i !== index)
    }))
  }

  const addBroughtAppliance = () => {
    setFormData((prev: any) => ({
      ...prev,
      broughtAppliances: [
        ...prev.broughtAppliances,
        { type: '' }
      ]
    }))
  }

  const removeBroughtAppliance = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      broughtAppliances: prev.broughtAppliances.filter((_: any, i: number) => i !== index)
    }))
  }

  const handleCancel = () => {
    if (!id) return
    
    setConfirmModal({
      isOpen: true,
      title: '予約のキャンセル',
      message: 'この予約をキャンセルしますか？\nこの操作は取り消せません。',
      confirmText: 'キャンセルする',
      cancelText: '戻る',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false })
        try {
          setLoading(true)
          await changeBookingStatus(id, 'canceled')
          
          // データを再取得
          const bookingData = await getBookingDetail(id)
          setBooking(bookingData)
          setMessageModal({
            isOpen: true,
            message: '予約をキャンセルしました。',
            type: 'success'
          })
        } catch (err) {
          console.error('Failed to cancel booking:', err)
          setMessageModal({
            isOpen: true,
            message: err instanceof Error ? err.message : '予約のキャンセルに失敗しました。',
            type: 'error'
          })
        } finally {
          setLoading(false)
        }
      }
    })
  }

  if (loading) {
    return (
      <MainLayout
        headerTitle="予約詳細"
        headerBreadcrumb="ホーム / 予約管理 / 詳細"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">読み込み中...</div>
        </div>
      </MainLayout>
    )
  }

  if (error || !booking) {
    return (
      <MainLayout
        headerTitle="予約詳細"
        headerBreadcrumb="ホーム / 予約管理 / 詳細"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500">{error || '予約データが見つかりません。'}</div>
        </div>
      </MainLayout>
    )
  }

  const fullAddress = [
    booking.serviceAddress.municipality,
    booking.serviceAddress.addressLine,
    booking.serviceAddress.building
  ].filter(Boolean).join('')

  const appliances = booking.broughtAppliances
    .map(appliance => getApplianceTypeLabel(appliance.type))
    .filter(type => type !== '-')
    .join(', ') || '-'

  return (
    <MainLayout
      headerTitle="予約詳細"
      headerBreadcrumb="ホーム / 予約管理 / 一覧 / 詳細"
    >
      {/* 戻るボタン */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium transition-all hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-sm focus:outline-none focus:ring-3 focus:ring-gray-500/10"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          一覧に戻る
        </button>
      </div>
      {/* 予約詳細情報 */}
      <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
        <div className="p-6">
          {!isEditMode ? (
            // 表示モード
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 予約基本情報 */}
              <InfoSection title="予約基本情報">
                <InfoRow label="予約ID" value={formatBookingId(booking.id)} />
                <InfoRow label="予約種類" value={getTypeLabel(booking.type)} />
                <InfoRow label="申請日時" value={booking.createdAt ? formatDateTime(booking.createdAt) : '-'} />
              </InfoSection>

            {/* 契約者情報 */}
            <InfoSection title="契約者情報">
              <InfoRow label="お名前" value={booking.contractor.name} />
              <InfoRow label="フリガナ" value={booking.contractor.nameKana} />
              <InfoRow label="生年月日" value={formatDate(booking.contractor.birthday)} />
              <InfoRow label="電話番号" value={booking.contractor.phoneNumber} />
              <InfoRow label="メールアドレス" value={booking.contractor.email} />
            </InfoSection>

            {/* 使用者情報 */}
            <InfoSection title="使用者情報">
              <InfoRow label="契約者との関係" value={getRelationToContractorLabel(booking.consumer.relationToContractor)} />
              <InfoRow label="お名前" value={booking.consumer.name} />
              <InfoRow label="フリガナ" value={booking.consumer.nameKana} />
              <InfoRow label="生年月日" value={formatDate(booking.consumer.birthday)} />
              <InfoRow label="電話番号" value={booking.consumer.phoneNumber} />
              <InfoRow label="メールアドレス" value={booking.consumer.email || '-'} />
            </InfoSection>

            {/* 住所情報 */}
            <InfoSection title="住所情報">
              <InfoRow label="郵便番号" value={booking.serviceAddress.zipCode} />
              <InfoRow label="市区町村" value={booking.serviceAddress.municipality} />
              <InfoRow label="番地" value={booking.serviceAddress.addressLine} />
              <InfoRow label="建物名" value={booking.serviceAddress.building} />
              <InfoRow label="建物の種類" value={getBuildingTypeLabel(booking.serviceAddress.buildingType)} />
            </InfoSection>

            {/* 当日立会人情報 */}
            <InfoSection title="当日立会人情報">
              <InfoRow label="契約者との関係" value={getRelationToContractorLabel(booking.attendee.relationToContractor)} />
              <InfoRow label="お名前" value={booking.attendee.name} />
              <InfoRow label="フリガナ" value={booking.attendee.nameKana} />
              <InfoRow label="電話番号" value={booking.attendee.phoneNumber} />
            </InfoSection>

            {/* その他情報 */}
            <InfoSection title="その他情報">
              <InfoRow label="お客様番号" value={booking.customerNumber.customerNumber} />
              <InfoRow label="支払い方法" value={getPaymentMethodLabel(booking.contractor.paymentMethod)} />
              <InfoRow label="請求書送付先" value={formatBillingAddress(booking.contractor.altBillingAddress)} copyable={false} />
              <InfoRow label="引っ越し先住所" value={formatBillingAddress(booking.contractor.movingAddress)} copyable={false} />
              <InfoRow label="引っ越し先電話番号" value={booking.contractor.movingPhone || '-'} />
              <InfoRow label="ガスメーターまでの鍵" value={getBooleanLabel(booking.contractor.lockGate)} />
              <InfoRow label="機器撤去" value={getBooleanLabel(booking.contractor.applianceRemoval)} />
              <InfoRow label="持参機器" value={appliances} copyable={false} />
              <InfoRow label="備考" value={booking.contractor.notes} copyable={false} />
            </InfoSection>
          </div>
          ) : (
            // 編集モード
            formData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 予約基本情報 */}
                <InfoSection title="予約基本情報">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">予約ID</label>
                      <div className="text-gray-900">{formatBookingId(booking.id)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">予約種類</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="open">開栓</option>
                        <option value="close">閉栓</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">申請日時</label>
                      <div className="text-gray-900">{booking.createdAt ? formatDateTime(booking.createdAt) : '-'}</div>
                    </div>
                  </div>
                </InfoSection>

                {/* 契約者情報 */}
                <InfoSection title="契約者情報">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">お名前</label>
                      <input
                        type="text"
                        value={formData.contractor.name || ''}
                        onChange={(e) => updateFormField('contractor', 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">フリガナ</label>
                      <input
                        type="text"
                        value={formData.contractor.nameKana || ''}
                        onChange={(e) => updateFormField('contractor', 'nameKana', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">生年月日</label>
                      <input
                        type="date"
                        value={formData.contractor.birthday ? (formData.contractor.birthday.includes('T') ? formData.contractor.birthday.split('T')[0] : formData.contractor.birthday) : ''}
                        onChange={(e) => updateFormField('contractor', 'birthday', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">電話番号</label>
                      <input
                        type="text"
                        value={formData.contractor.phoneNumber || ''}
                        onChange={(e) => updateFormField('contractor', 'phoneNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">メールアドレス</label>
                      <input
                        type="email"
                        value={formData.contractor.email || ''}
                        onChange={(e) => updateFormField('contractor', 'email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">支払い方法</label>
                      <select
                        value={formData.contractor.paymentMethod || ''}
                        onChange={(e) => updateFormField('contractor', 'paymentMethod', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">選択してください</option>
                        <option value="direct_debit">口座振替</option>
                        <option value="bank_transfer">銀行振込</option>
                        <option value="convenience_store">コンビニ払い</option>
                        <option value="invoice">請求書</option>
                        <option value="other">その他</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">備考</label>
                      <textarea
                        value={formData.contractor.notes || ''}
                        onChange={(e) => updateFormField('contractor', 'notes', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </InfoSection>

                {/* 使用者情報 */}
                <InfoSection title="使用者情報">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">契約者との関係</label>
                      <select
                        value={formData.consumer.relationToContractor || ''}
                        onChange={(e) => updateFormField('consumer', 'relationToContractor', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">選択してください</option>
                        <option value="self">本人</option>
                        <option value="family">家族</option>
                        <option value="landlord">大家</option>
                        <option value="manager">管理人</option>
                        <option value="other">その他</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">お名前</label>
                      <input
                        type="text"
                        value={formData.consumer.name || ''}
                        onChange={(e) => updateFormField('consumer', 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">フリガナ</label>
                      <input
                        type="text"
                        value={formData.consumer.nameKana || ''}
                        onChange={(e) => updateFormField('consumer', 'nameKana', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">生年月日</label>
                      <input
                        type="date"
                        value={formData.consumer.birthday ? (formData.consumer.birthday.includes('T') ? formData.consumer.birthday.split('T')[0] : formData.consumer.birthday) : ''}
                        onChange={(e) => updateFormField('consumer', 'birthday', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">電話番号</label>
                      <input
                        type="text"
                        value={formData.consumer.phoneNumber || ''}
                        onChange={(e) => updateFormField('consumer', 'phoneNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </InfoSection>

                {/* 住所情報 */}
                <InfoSection title="住所情報">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">郵便番号</label>
                      <input
                        type="text"
                        value={formData.serviceAddress.zipCode || ''}
                        onChange={(e) => updateFormField('serviceAddress', 'zipCode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">市区町村</label>
                      <input
                        type="text"
                        value={formData.serviceAddress.municipality || ''}
                        onChange={(e) => updateFormField('serviceAddress', 'municipality', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">番地</label>
                      <input
                        type="text"
                        value={formData.serviceAddress.addressLine || ''}
                        onChange={(e) => updateFormField('serviceAddress', 'addressLine', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">建物名</label>
                      <input
                        type="text"
                        value={formData.serviceAddress.building || ''}
                        onChange={(e) => updateFormField('serviceAddress', 'building', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </InfoSection>

                {/* 当日立会人情報 */}
                <InfoSection title="当日立会人情報">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">契約者との関係</label>
                      <select
                        value={formData.attendee.relationToContractor || ''}
                        onChange={(e) => updateFormField('attendee', 'relationToContractor', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">選択してください</option>
                        <option value="self">本人</option>
                        <option value="family">家族</option>
                        <option value="landlord">大家</option>
                        <option value="manager">管理人</option>
                        <option value="other">その他</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">お名前</label>
                      <input
                        type="text"
                        value={formData.attendee.name || ''}
                        onChange={(e) => updateFormField('attendee', 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">フリガナ</label>
                      <input
                        type="text"
                        value={formData.attendee.nameKana || ''}
                        onChange={(e) => updateFormField('attendee', 'nameKana', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">電話番号</label>
                      <input
                        type="text"
                        value={formData.attendee.phoneNumber || ''}
                        onChange={(e) => updateFormField('attendee', 'phoneNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </InfoSection>

                {/* その他情報 */}
                <InfoSection title="その他情報">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">お客様番号</label>
                      <input
                        type="text"
                        value={formData.customerNumber.customerNumber || ''}
                        onChange={(e) => updateFormField('customerNumber', 'customerNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </InfoSection>
              </div>
            )
          )}
        </div>
      </section>

      {/* 希望日・時間帯カード */}
      {!isEditMode ? (
        <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-900 m-0">希望日・時間帯</h2>
          </div>
          <div className="p-6">
            <PreferredSlots
              slots={booking.preferredSlots}
              status={booking.status}
              requiresAttendee={booking.requiresAttendee}
              onApprove={handleApproveSlot}
            />
          </div>
        </section>
      ) : (
        formData && (
          <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
            <div className="p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-900 m-0">希望日・時間帯</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {formData.preferredSlots.map((slot: any, index: number) => (
                  <div key={index} className="flex gap-4 items-end border-b border-gray-200 pb-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">希望日</label>
                      <input
                        type="date"
                        value={slot.date || ''}
                        onChange={(e) => updateFormArray('preferredSlots', index, 'date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">時間帯</label>
                      <select
                        value={slot.time_slot || ''}
                        onChange={(e) => updateFormArray('preferredSlots', index, 'time_slot', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">選択してください</option>
                        <option value="morning">9:00～11:00</option>
                        <option value="midday">11:00～12:00</option>
                        <option value="afternoon">13:00～15:00</option>
                        <option value="evening">15:00～17:00</option>
                      </select>
                    </div>
                    <div className="w-24">
                      <label className="block text-sm font-medium text-gray-700 mb-1">優先順位</label>
                      <input
                        type="number"
                        min="1"
                        max="3"
                        value={slot.preference_order || ''}
                        onChange={(e) => updateFormArray('preferredSlots', index, 'preference_order', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    {formData.preferredSlots.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePreferredSlot(index)}
                        className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                      >
                        削除
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addPreferredSlot}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                >
                  希望日を追加
                </button>
              </div>
            </div>
          </section>
        )
      )}

      {/* 持ち込み機器カード */}
      {isEditMode && formData && (
        <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-900 m-0">持ち込みガス機器</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {formData.broughtAppliances.map((app: any, index: number) => (
                <div key={index} className="flex gap-4 items-end border-b border-gray-200 pb-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">機器種類</label>
                    <select
                      value={app.type || ''}
                      onChange={(e) => updateFormArray('broughtAppliances', index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">選択してください</option>
                      <option value="gas_stove">ガスコンロ</option>
                      <option value="gas_water_heater">ガス給湯器</option>
                      <option value="gas_heating">ガス暖房</option>
                      <option value="other">その他</option>
                    </select>
                  </div>
                  {formData.broughtAppliances.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBroughtAppliance(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                    >
                      削除
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addBroughtAppliance}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
              >
                機器を追加
              </button>
            </div>
          </div>
        </section>
      )}

      {/* アクションボタンカード */}
      <section className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-8">
        <div className="p-6">
          {!isEditMode ? (
            <ActionButtons
              status={booking.status}
              onStatusChange={handleStatusChange}
              onEdit={handleEdit}
              onCancel={handleCancel}
            />
          ) : (
            <div className="flex gap-4 justify-end pt-6 border-t border-gray-200">
              <button
                onClick={handleCancelEdit}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-900 border border-gray-200 rounded-lg text-sm font-medium transition-all hover:bg-gray-200 hover:-translate-y-0.5 focus:outline-none focus:ring-3 focus:ring-gray-500/10 min-h-[2.5rem]"
              >
                キャンセル
              </button>
              <button
                onClick={handleUpdate}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium transition-all hover:bg-blue-600 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-3 focus:ring-blue-500/10 min-h-[2.5rem] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '更新中...' : '更新する'}
              </button>
            </div>
          )}
        </div>
      </section>
      
      {/* 確認モーダル */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
      
      {/* 料金未払い・リース契約確認モーダル */}
      <ConfirmModal
        isOpen={paymentCheckModal.isOpen}
        title="承認前の確認"
        message="料金の未払い、ガス機器のリース契約はありませんか？もし、いずれかが該当する場合は、承認をキャンセルして架電リストに追加してください。"
        confirmText="承認へ進む"
        cancelText="キャンセル"
        variant="default"
        onConfirm={handleProceedToAttendeeSelection}
        onCancel={() => setPaymentCheckModal({ ...paymentCheckModal, isOpen: false })}
      />
      
      {/* 立合い選択モーダル */}
      {attendeeSelectionModal.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
            onClick={() => setAttendeeSelectionModal({ ...attendeeSelectionModal, isOpen: false })}
          ></div>
          <div className="relative bg-white rounded-xl shadow-lg max-w-md w-[90%] p-6 animate-[modalSlideIn_0.3s_ease]">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">立合いの選択</h3>
            <div className="mb-6">
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="requiresAttendee"
                    checked={attendeeSelectionModal.requiresAttendee === true}
                    onChange={() => setAttendeeSelectionModal({ ...attendeeSelectionModal, requiresAttendee: true })}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">立合いあり</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="requiresAttendee"
                    checked={attendeeSelectionModal.requiresAttendee === false}
                    onChange={() => setAttendeeSelectionModal({ ...attendeeSelectionModal, requiresAttendee: false })}
                    className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">立合いなし</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="inline-flex items-center justify-center px-5 py-2.5 bg-gray-100 text-gray-900 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
                onClick={() => setAttendeeSelectionModal({ ...attendeeSelectionModal, isOpen: false })}
              >
                キャンセル
              </button>
              <button
                className="inline-flex items-center justify-center px-5 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleProceedToFinalConfirmation}
                disabled={attendeeSelectionModal.requiresAttendee === null}
              >
                次へ
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* メッセージモーダル */}
      <MessageModal
        isOpen={messageModal.isOpen}
        message={messageModal.message}
        type={messageModal.type}
        onClose={() => setMessageModal({ ...messageModal, isOpen: false })}
      />
    </MainLayout>
  )
}

export default BookingDetail

